import React, { useEffect, useMemo, useState } from 'react';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import moment from 'moment';
import PageSelect from 'components/Selects/PageSelect';
import { ROLES } from 'appConstants';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useSelector, useDispatch } from 'react-redux';
import { useAsyncFn } from 'react-use';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useHistory } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import {
  getScheduledTrainingSessions,
  getScheduledTrainingSessionsByStation,
  getFlexibleTrainingSessions,
  getFlexibleTrainingSessionsByStation,
} from 'http/trainingSessions';
import { actions as modalActions } from 'redux/modal';
import Toolbar from './Toolbar';
import transformToSchedulerData from './helpers/transformToSchedulerData';
import transformToSchedulerDataDaily from './helpers/transformToSchedulerDataDaily';
import TrainingSessionView from './TrainingSessionView';
import FlexibleSessionsPanel from './FlexibleSessionsPanel';

const localizer = momentLocalizer(moment);

const DELIVERY_METHODS = ['classroom', 'virtual_classroom', 'elearning', 'on_the_job'];

const useStyles = makeStyles(theme => ({
  content: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(6, 8, 4, 8),
  },
  layout: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  calendarContainer: {
    flex: 1,
    minWidth: 0,
    height: '800px',

    '& .rbc-header + .rbc-header': {
      borderLeft: 'none',
    },

    '& .rbc-month-view': {
      borderTopLeftRadius: '20px',
      borderTopRightRadius: '20px',

      '& .rbc-show-more': {
        color: theme.palette.primary.main,
      },
      '& .rbc-off-range-bg': {
        background: '#fff',
      },
      '& .rbc-month-header': {
        background: '#7B7E8E',
        color: '#fff',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
      },

      '& .rbc-header': {
        padding: '15px 5px',
      },
    },
  },
  headerFilters: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
}));

const sessionColors = {
  classroom: '#3D6FEE',
  virtual_classroom: '#9C27B0',
  on_the_job: '#FF9800',
  elearning: '#4CAF50',
  default: '#3D6FEE',
};

const TrainingSessionSchedule = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { station, role } = useSelector(state => state.auth.profile);
  const stationId = station ? station.id : undefined;

  const deliveryMethodOptions = useMemo(
    () => [
      { label: t('trainingSessionSchedule.allDeliveryMethods'), value: 'all' },
      ...DELIVERY_METHODS.map(value => ({
        label: t(`trainingSessions.deliveryMethods.${value}`),
        value,
      })),
    ],
    [t],
  );

  const availabilityOptions = useMemo(
    () => [
      { label: t('schedule.onlineActive'), value: 'active' },
      { label: t('schedule.onlineUpcoming'), value: 'upcoming' },
      { label: t('schedule.onlineAll'), value: 'all' },
    ],
    [t],
  );

  const [deliveryMethod, setDeliveryMethod] = useState('all');
  const [availability, setAvailability] = useState('active');
  const [sessions, setSessions] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [flexibleSessions, setFlexibleSessions] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');

  const showFlexiblePanel = deliveryMethod === 'all' || deliveryMethod === 'elearning';

  const permissions = {
    addSession: [ROLES.SUPER, ROLES.ADMIN],
    editSession: [ROLES.SUPER, ROLES.ADMIN],
  };

  const buildScheduleParams = (date, view, method, availabilityFilter) => {
    const startOfMonth = moment(date).startOf('month');
    const endOfMonth = moment(date).endOf('month');
    const firstDayOfCalendar = startOfMonth.clone().subtract(startOfMonth.day(), 'days');
    const lastDayOfCalendar = endOfMonth.clone().add(6 - endOfMonth.day(), 'days');

    const params = {
      'date-from': firstDayOfCalendar.format('YYYY-MM-DD'),
      'date-to': lastDayOfCalendar.format('YYYY-MM-DD'),
      availability: availabilityFilter,
    };

    if (method && method !== 'all') {
      params.deliveryMethod = method;
    }

    return { params, view };
  };

  const fetchScheduledSessions = async (method, availabilityFilter, date, view, stationIdParam) => {
    const { params } = buildScheduleParams(date, view, method, availabilityFilter);

    if (role === ROLES.TRAINER) {
      return getScheduledTrainingSessions(params);
    }

    return getScheduledTrainingSessionsByStation(stationIdParam, params);
  };

  const fetchFlexibleSessionsList = async (method, availabilityFilter, stationIdParam) => {
    const params = { availability: availabilityFilter };

    if (method && method !== 'all' && method !== 'elearning') {
      return [];
    }

    if (method === 'elearning') {
      params.deliveryMethod = 'elearning';
    }

    if (role === ROLES.TRAINER) {
      const result = await getFlexibleTrainingSessions(params);
      return Array.isArray(result) ? result : [];
    }

    const result = await getFlexibleTrainingSessionsByStation(stationIdParam, params);
    return Array.isArray(result) ? result : [];
  };

  const [fetchingSessionsState, fetchSessions] = useAsyncFn(
    async (method, availabilityFilter, date, view, stationIdParam) => {
      const sessionsData = await fetchScheduledSessions(method, availabilityFilter, date, view, stationIdParam);

      if (!sessionsData?.error) {
        const relatedSessions = Array.isArray(sessionsData) ? sessionsData : [];
        const transformedSessions =
          view === 'month' ? transformToSchedulerData(relatedSessions) : transformToSchedulerDataDaily(relatedSessions);

        setSessions(relatedSessions);
        setCalendarEvents(transformedSessions);
      }
    },
  );

  const [fetchingFlexibleState, fetchFlexibleSessions] = useAsyncFn(
    async (method, availabilityFilter, stationIdParam) => {
      if (!showFlexiblePanel) {
        setFlexibleSessions([]);
        return;
      }

      const relatedSessions = await fetchFlexibleSessionsList(method, availabilityFilter, stationIdParam);
      setFlexibleSessions(relatedSessions);
    },
  );

  const handleNavigate = (date, view, stationIdParam) => {
    setCurrentDate(date);
    fetchSessions(deliveryMethod, availability, date, view, stationIdParam);
  };

  const redirectToEditSession = sessionId => {
    history.push(`${routesAliases.editTrainingSession}/${sessionId}`, {
      backUrl: routesAliases.trainingSessionSchedule,
    });
  };

  const showSessionInfo = sessionInfo => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingSessions.pageTitle'),
        },
        <TrainingSessionView session={sessionInfo} />,
      ),
    );
  };

  const handleActionOnSelectSession = (actionPermissions, session) => {
    if (!role) {
      return;
    }

    if (actionPermissions.includes(role)) {
      redirectToEditSession(session.id);
    } else {
      showSessionInfo(session);
    }
  };

  const handleSelectCalendarEvent = event => {
    handleActionOnSelectSession(permissions.editSession, event.sessionInfo);
  };

  const handleSelectFlexibleSession = session => {
    handleActionOnSelectSession(permissions.editSession, session);
  };

  const handleChangeDeliveryMethod = value => {
    setDeliveryMethod(value);
  };

  const handleChangeAvailability = value => {
    setAvailability(value);
  };

  const handleChangeView = view => {
    setCurrentView(view);
  };

  useEffect(() => {
    handleNavigate(currentDate, currentView, stationId);
  }, [stationId, currentView, deliveryMethod, availability]);

  useEffect(() => {
    fetchFlexibleSessions(deliveryMethod, availability, stationId);
  }, [deliveryMethod, availability, stationId, showFlexiblePanel]);

  useEffect(() => {
    if (sessions.length) {
      const transformedSessions =
        currentView === 'month' ? transformToSchedulerData(sessions) : transformToSchedulerDataDaily(sessions);
      setCalendarEvents(transformedSessions);
    }
  }, [currentView, sessions]);

  const isLoading = fetchingSessionsState.loading;

  return (
    <PageLayout
      pageTitle={t('trainingSessionSchedule.pageTitle')}
      classes={{
        content: classes.content,
      }}
      headerActions={[
        {
          type: 'component',
          Component: (
            <Box className={classes.headerFilters}>
              <PageSelect
                onChangeValue={handleChangeDeliveryMethod}
                selectedOption={deliveryMethod}
                options={deliveryMethodOptions}
              />
              <PageSelect
                onChangeValue={handleChangeAvailability}
                selectedOption={availability}
                options={availabilityOptions}
              />
            </Box>
          ),
        },
        {
          type: 'button',
          icon: 'add',
          title: t('trainingSessions.actionAdd'),
          isHidden: !role || !permissions.addSession.includes(role),
          onClick: () => {
            history.push(routesAliases.createTrainingSession, {
              backUrl: routesAliases.trainingSessionSchedule,
            });
          },
        },
      ]}
    >
      <Box className={classes.layout}>
        <ModalBusyContainer busy={isLoading}>
          <div className={classes.calendarContainer}>
            <Calendar
              popup
              selectable
              events={calendarEvents}
              views={{
                month: true,
                week: true,
                day: true,
              }}
              step={60}
              eventPropGetter={event => ({
                style: {
                  background: sessionColors[event.type] || sessionColors.default,
                  padding: '3px',
                },
              })}
              formats={{
                weekdayFormat: 'dddd',
              }}
              components={{
                toolbar: Toolbar,
              }}
              onView={handleChangeView}
              onNavigate={date => handleNavigate(date, currentView, stationId)}
              onSelectEvent={handleSelectCalendarEvent}
              date={currentDate}
              view={currentView}
              localizer={localizer}
            />
          </div>
        </ModalBusyContainer>
        {showFlexiblePanel && (
          <FlexibleSessionsPanel
            sessions={flexibleSessions}
            onSelectSession={handleSelectFlexibleSession}
            loading={fetchingFlexibleState.loading}
          />
        )}
      </Box>
    </PageLayout>
  );
};

export default TrainingSessionSchedule;
